import { useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth'
import { Login } from './pages/Login'
import { Dashboard } from './pages/Dashboard'
import { Intake } from './pages/Intake'
import { Inventory } from './pages/Inventory'
import { Search } from './pages/Search'
import { Batches } from './pages/Batches'
import { Draft } from './pages/Draft'
import { Extractor } from './pages/Extractor'
import { Retail } from './pages/Retail'
import { Audit } from './pages/Audit'
import { System } from './pages/System'
import { Settings } from './pages/Settings'
import { Huntly } from './pages/Huntly'
import { Fusion } from './pages/Fusion'
import { FusionConfig } from './pages/FusionConfig'
import { Finish } from './pages/Finish'
import { TelegramApp } from './pages/TelegramApp'
import {
  IconAudit,
  IconBatches,
  IconChevron,
  IconDashboard,
  IconDraft,
  IconExtract,
  IconFinish,
  IconFusion,
  IconHuntly,
  IconIntake,
  IconInventory,
  IconLogout,
  IconRetail,
  IconSearch,
  IconSettings,
  IconSystem,
} from './components/icons'

// 导航按「看什么 / 做什么 / 管什么」分组：九个平级项目会让人每次都要扫一遍。
// to 与 label 保持原样，路由表未改动。
type NavItem = { to: string; label: string; end?: boolean; icon: typeof IconDashboard }

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: '概览',
    items: [{ to: '/dashboard', label: '看板', end: true, icon: IconDashboard }],
  },
  {
    label: '日常作业',
    items: [
      { to: '/intake', label: '入库', icon: IconIntake },
      { to: '/inventory', label: '域&Domain总库', icon: IconInventory },
      { to: '/search', label: '搜索', icon: IconSearch },
      { to: '/draft', label: '草稿表格', icon: IconDraft },
      { to: '/extractor', label: '提取器', icon: IconExtract },
      { to: '/retail', label: '下单记录', icon: IconRetail },
    ],
  },
  {
    label: '解析',
    items: [
      { to: '/huntly', label: '猎域', icon: IconHuntly },
      { to: '/fusion', label: '融合器', icon: IconFusion },
      { to: '/fusion-config', label: '配置', icon: IconSettings },
    ],
  },
  {
    label: '一键完成',
    items: [{ to: '/finish', label: '一键完成', icon: IconFinish }],
  },
  {
    label: '管理',
    items: [
      { to: '/batches', label: '批次 / 履历', icon: IconBatches },
      { to: '/audit', label: '审计', icon: IconAudit },
      { to: '/system', label: '系统', icon: IconSystem },
      { to: '/settings', label: '设置', icon: IconSettings },
    ],
  },
]

const RAIL_KEY = 'ds.nav.rail'

// localStorage 在隐私模式/被禁用时读写都会抛，读失败就按「展开」处理，写失败只是记不住。
function readRail(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === '1'
  } catch {
    return false
  }
}
function writeRail(next: boolean) {
  try {
    localStorage.setItem(RAIL_KEY, next ? '1' : '0')
  } catch {
    /* 记不住就这一页生效，不影响任何业务 */
  }
}

export function App() {
  const { user, ready, logout } = useAuth()
  const [rail, setRail] = useState(readRail)
  const location = useLocation()
  const mini = location.pathname === '/tg'

  if (!ready) return <div className="center-screen">载入中…</div>
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/tg" element={<TelegramApp />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }
  if (mini) {
    return (
      <main className="tg-app" id="main">
        <Finish />
      </main>
    )
  }

  function toggleRail() {
    const next = !rail
    setRail(next)
    writeRail(next)
  }

  return (
    <div className={rail ? 'shell rail' : 'shell'}>
      <a className="skip-link" href="#main">
        跳到主内容
      </a>
      <aside className={rail ? 'side rail' : 'side'}>
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <IconInventory size={17} />
          </span>
          <span className="brand-text rail-hide">
            域名库存
            <small>零售履历操作台</small>
          </span>
          <button
            type="button"
            className="rail-toggle"
            onClick={toggleRail}
            title={rail ? '展开侧栏' : '收起侧栏'}
            aria-label={rail ? '展开侧栏' : '收起侧栏'}
            aria-pressed={rail}
          >
            <IconChevron size={16} />
          </button>
        </div>
        <nav aria-label="主导航">
          {NAV_GROUPS.map((g) => (
            <div className="nav-group" key={g.label}>
              <div className="nav-label rail-hide" aria-hidden="true">
                {g.label}
              </div>
              {g.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  title={n.label}
                  className={({ isActive }) => (isActive ? 'active' : '')}
                >
                  <n.icon />
                  <span className="rail-hide">{n.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="side-foot">
          <div className="who rail-hide">
            <b title={user.username}>{user.username}</b>
            <span className={`badge ${user.canWrite ? 'badge-ok' : 'badge-muted'}`}>{user.role}</span>
          </div>
          <button type="button" title="退出登录" onClick={() => void logout()}>
            <span className="rail-hide">退出登录</span>
            {rail && <IconLogout size={16} />}
          </button>
        </div>
      </aside>
      <main className="content" id="main" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/intake" element={<Intake />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/search" element={<Search />} />
          <Route path="/batches" element={<Batches />} />
          <Route path="/draft" element={<Draft />} />
          <Route path="/extractor" element={<Extractor />} />
          <Route path="/retail" element={<Retail />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/system" element={<System />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/huntly" element={<Huntly />} />
          <Route path="/fusion" element={<Fusion />} />
          <Route path="/fusion-config" element={<FusionConfig />} />
          <Route path="/finish" element={<Finish />} />
          <Route path="*" element={<div className="empty">页面不存在</div>} />
        </Routes>
      </main>
    </div>
  )
}
